import React, { useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, InfoIcon } from 'lucide-react';
import { toast } from 'sonner';
import { AvailabilityData } from '../data/types';
interface BookingCalendarProps {
  productId: string;
  selectedStartDate: string;
  selectedEndDate: string;
  availability: AvailabilityData;
  isLoading: boolean;
  onSelectRange: (start: string, end: string) => void;
}
const monthNames = ['Siječanj', 'Veljača', 'Ožujak', 'Travanj', 'Svibanj', 'Lipanj', 'Srpanj', 'Kolovoz', 'Rujan', 'Listopad', 'Studeni', 'Prosinac'];
const dayNames = ['Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub', 'Ned'];
export function BookingCalendar({
  productId,
  selectedStartDate,
  selectedEndDate,
  availability,
  isLoading,
  onSelectRange
}: BookingCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const availabilityByDate = useMemo(() => availability, [availability]);
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const startingDay = firstDay === 0 ? 6 : firstDay - 1;
  const getDayStatus = (date: string): 'blocked' | 'pending' | 'confirmed' | null => {
    if (!productId) return null;
    const isBlocked = availabilityByDate.blockedDates.some(block => (!block.productId || block.productId === productId) && isDateInRange(date, block.date, block.endDate || block.date));
    if (isBlocked) return 'blocked';
    const bookings = availabilityByDate.bookings.filter(booking => booking.productId === productId && isDateInRange(date, booking.date, booking.endDate || booking.date));
    if (bookings.some(booking => booking.status === 'confirmed')) return 'confirmed';
    if (bookings.some(booking => booking.status === 'pending')) return 'pending';
    return null;
  };
  const isUnavailable = (date: string): boolean => getDayStatus(date) !== null;
  const isRangeFree = (start: string, end: string): boolean => {
    let cursor = new Date(`${start}T00:00:00`);
    const last = new Date(`${end}T00:00:00`);
    while (cursor <= last) {
      const date = toDateString(cursor);
      if (isUnavailable(date)) return false;
      cursor.setDate(cursor.getDate() + 1);
    }
    return true;
  };
  const handleDayClick = (date: string) => {
    if (!selectedStartDate || selectedEndDate || date < selectedStartDate) {
      onSelectRange(date, '');
      return;
    }
    if (date === selectedStartDate) {
      onSelectRange(date, '');
      return;
    }
    if (!isRangeFree(selectedStartDate, date)) {
      toast.error('Odabrani raspon sadrži zauzet dan. Odaberite drugi raspon.');
      return;
    }
    onSelectRange(selectedStartDate, date);
  };
  const isPast = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const previousMonth = () => {
    const previous = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    const today = new Date();
    if (previous.getFullYear() < today.getFullYear() || previous.getFullYear() === today.getFullYear() && previous.getMonth() < today.getMonth()) return;
    setCurrentMonth(previous);
  };
  return <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100" aria-busy={isLoading} data-id="element-29" data-mp="1u0c6nhga7ysi">
      <div className="flex justify-between items-center mb-6" data-id="element-30" data-mp="1p0zc0aj65nfd">
        <button type="button" onClick={previousMonth} aria-label="Prethodni mjesec" className="p-2 rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary" data-id="element-31" data-mp="23yrosuxbzxpv">
          <ChevronLeftIcon size={24} data-id="element-32" data-mp="9lk1rpflkqes" />
        </button>
        <h3 className="text-xl font-bold text-gray-800" data-id="element-33" data-mp="4m77ijmech26">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button type="button" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} aria-label="Sljedeći mjesec" className="p-2 rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary" data-id="element-34" data-mp="2rtv1tc0xawnn">
          <ChevronRightIcon size={24} data-id="element-35" data-mp="2e2ztq1y90jl4" />
        </button>
      </div>
      {isLoading ? <p className="py-10 text-center text-sm text-gray-500" role="status" data-id="element-36" data-mp="2i4lbo1pn3o26">
          Učitavamo dostupnost termina…
        </p> : <>
          <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-37" data-mp="2mw3c8onr3mmm">
            {dayNames.map(day => <div key={day} className="text-center text-xs font-bold text-gray-400 uppercase" data-id="element-38" data-mp="1qcm7is2cheyg">
                {day}
              </div>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-id="element-39" data-mp="1muya8hyjtqij">
            {Array.from({
          length: startingDay
        }).map((_, index) => <div key={`empty-${index}`} className="h-12 w-12 sm:h-14 sm:w-14" data-id="element-40" data-mp="1dujnde5xs3cx" data-mpi={index} />)}
            {Array.from({
          length: daysInMonth
        }).map((_, index) => {
          const day = index + 1;
          const dateObject = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
          const date = toDateString(dateObject);
          const endpoint = date === selectedStartDate || date === selectedEndDate;
          const inRange = Boolean(selectedStartDate && selectedEndDate && date > selectedStartDate && date < selectedEndDate);
          const dayStatus = getDayStatus(date);
          const unavailable = dayStatus !== null;
          const past = isPast(dateObject);
          const disabled = past || unavailable;
          const style = endpoint ? 'bg-gray-900 text-white scale-110' : inRange ? 'bg-primary/15 text-primary' : past ? 'bg-gray-100 text-gray-400 line-through cursor-not-allowed' : dayStatus === 'pending' ? 'bg-yellow-100 text-yellow-800 cursor-not-allowed' : dayStatus === 'confirmed' || dayStatus === 'blocked' ? 'bg-red-100 text-red-700 cursor-not-allowed' : 'bg-green-50 text-green-700 hover:bg-green-100';
          const statusLabel = dayStatus === 'pending' ? 'Rezervacija čeka potvrdu' : dayStatus === 'confirmed' ? 'Rezervirano' : dayStatus === 'blocked' ? 'Termin nije dostupan' : undefined;
          return <div key={date} className="flex justify-center p-1" data-id="element-41" data-mp="37fq04gn1rtv8" data-mpi={index}>
                  <button type="button" disabled={disabled} onClick={() => handleDayClick(date)} aria-label={statusLabel ? `${day}. ${statusLabel}` : `${day}. Slobodan termin`} className={`h-12 w-12 sm:h-14 sm:w-14 rounded-full text-sm font-bold transition-transform focus:outline-none focus:ring-2 focus:ring-primary ${style}`} data-id="element-42" data-mp="37ewp33h22i7a">
                    {day}
                  </button>
                </div>;
        })}
          </div>
        </>}
      <div className="mt-6 flex flex-wrap justify-center gap-4 border-t border-gray-100 pt-4 text-sm text-gray-600" data-id="element-43" data-mp="3v4ions1cazft">
        <span className="flex items-center gap-2" data-id="element-44" data-mp="1sk6i8i9kv4gp">
          <i className="h-4 w-4 rounded-full border border-green-200 bg-green-50" data-id="element-45" data-mp="1xh6qmztwgjaz" />
          Slobodno
        </span>
        <span className="flex items-center gap-2" data-id="element-46" data-mp="22frst4sppd22">
          <i className="h-4 w-4 rounded-full border border-yellow-300 bg-yellow-100" data-id="element-47" data-mp="22eyijtujo5f6" />
          Čeka potvrdu
        </span>
        <span className="flex items-center gap-2" data-id="element-48" data-mp="2b9d4gf9kye79">
          <i className="h-4 w-4 rounded-full border border-red-200 bg-red-100" data-id="element-49" data-mp="2b8v1z0wbr3qw" />
          Rezervirano
        </span>
        <span className="flex items-center gap-2" data-id="element-50" data-mp="2gaakomb14y64">
          <i className="h-4 w-4 rounded-full bg-gray-900" data-id="element-51" data-mp="2l8c5pcs0n7k8" />
          Odabrano
        </span>
      </div>
      {!productId && <div className="mt-4 rounded-xl bg-blue-50 p-3 text-sm text-blue-700 flex gap-2" data-id="element-52" data-mp="2q5m3pgth0rjo">
          <InfoIcon size={16} data-id="element-53" data-mp="quugzmfvk4nm" />
          <p data-id="element-54" data-mp="qvmckx9h22dw">
            Prvo odaberite proizvod kako biste vidjeli dostupnost po datumima.
          </p>
        </div>}
    </div>;
}
function toDateString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function isDateInRange(date: string, start: string, end: string): boolean {
  return date >= start && date <= end;
}